import { FC, memo, useMemo } from 'react';

import FiltersBlock, {
  EntityFilter,
  getSelectedQueryParam,
} from '@components/ui/filters/FiltersBlock';
import PriceRangeFilter from '@components/ui/filters/PriceRangeFilter/PriceRangeFilter';
import NameFilter from '@components/ui/filters/NameFilter/NameFilter';
import SingleSelectionFilter from '@components/ui/filters/SingleSelectionFilter/SingleSelectionFilter';
import ClearAllFilters from '@components/views/components/ClearAllFilters/ClearAllFilters';
import { FlexibleDiv } from '@components/formStyles';
import { isEmptyFilters } from '@components/views/utils';
import ReviewFilter from '@components/ui/filters/ReviewFilter/ReviewFilter';
import ClassFilter from '@components/ui/filters/ClassFilter/ClassFilter';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';

import HotelFilterSection from './HotelFilterSection';
import {
  HotelFilterCard,
  HotelFilterSectionWrapper,
} from './HotelFilterSectionStyle';

const ListingFilters: FC<{
  hotelFilters: EntityFilter[] | undefined;
  isExpediaHotels: boolean;
  hotelsCount?: number;
  isHotelsLoading?: boolean;
  listingTitle?: string;
}> = ({ hotelFilters, listingTitle, isExpediaHotels }) => {
  const query = useSearchParamsToQuery();
  const pointFilter = useMemo(
    () => hotelFilters?.filter((item) => item?.type === 'point'),
    [hotelFilters]
  );
  const selectedRegions = useMemo(
    () => getSelectedQueryParam(query, 'region'),
    [query]
  );
  const selectedBoardTypes = useMemo(
    () => getSelectedQueryParam(query, 'boardType'),
    [query]
  );
  const selectedPopularities = useMemo(
    () => getSelectedQueryParam(query, 'popularity'),
    [query]
  );
  const selectedThemes = useMemo(
    () => getSelectedQueryParam(query, 'theme'),
    [query]
  );
  const selectedStars = useMemo(
    () => getSelectedQueryParam(query, 'star'),
    [query]
  );

  const selectedCategories = useMemo(
    () => getSelectedQueryParam(query, 'category'),
    [query]
  );

  const selectedFeatures = useMemo(
    () => getSelectedQueryParam(query, 'feature'),
    [query]
  );

  return (
    <FiltersBlock
      applicableFilters={hotelFilters}
      isWithShallow={true}
      activeService='hotel'
    >
      {({
        filters,
        freeCancellation,
        onRegionChecked,
        onlyAvailableHotels,
        handleSingleSelectionFilter,
        handleSelectedPopularities,
        popularities: features,
        keywords,
        setKeywords,
        handleKeywordsFilter,
        handleClearKeywordsFilter,
        minAccPriceValue,
        maxAccPriceValue,
        setMaxAccPriceValue,
        setMinAccPriceValue,
        handlePriceFilter,
        boardTypes,
        themes,
        handleSelectedThemes,
        handleSelectedAccTypes,
        handleClearAllFilters,
        handleSelectedTripAdvisor,
        stars,
        handleSelectedStars,
        categories,
        handleSelectedCategories,
        handleRemoveSpecificFilter,
        featureGroups,
        handleSelectedFeatures,
      }) => (
        <>
          <FlexibleDiv bottom={20}>
            <ClearAllFilters
              onClick={handleClearAllFilters}
              disabled={isEmptyFilters(query)}
              text='Tüm Filtreleri Temizle'
              variant='card'
            />
          </FlexibleDiv>

          {isExpediaHotels ? (
            <>
              {stars?.length ? (
                <HotelFilterSectionWrapper>
                  <ClassFilter
                    title='Otel Sınıfı'
                    data={stars}
                    selectedItems={selectedStars}
                    handleSelectedItems={handleSelectedStars}
                  />
                </HotelFilterSectionWrapper>
              ) : null}

              <HotelFilterCard>
                <NameFilter
                  inputWrapperClassName='listing-hotel-name-filter'
                  title='Otel Adı'
                  name={keywords}
                  setName={setKeywords}
                  placeholder='Otel adı ile arayın'
                  handleSearch={handleKeywordsFilter}
                  handleClearKeywordsFilter={handleClearKeywordsFilter}
                  withClear
                />
              </HotelFilterCard>

              {pointFilter?.[0]?.items?.length ? (
                <HotelFilterCard>
                  <ReviewFilter
                    title='Misafir Puanı'
                    point={pointFilter}
                    handleSelectedItems={handleSelectedTripAdvisor}
                    selectedItems={query?.point || ''}
                    variant='text'
                  />
                </HotelFilterCard>
              ) : null}

              <HotelFilterSectionWrapper>
                <PriceRangeFilter
                  minValue={minAccPriceValue}
                  maxValue={maxAccPriceValue}
                  setMinValue={setMinAccPriceValue}
                  setMaxValue={setMaxAccPriceValue}
                  title='Konaklama Fiyatı'
                  handleSearch={handlePriceFilter}
                  collapsible
                />
              </HotelFilterSectionWrapper>
            </>
          ) : (
            <>
              {filters?.regions?.length ? (
                <HotelFilterSection
                  title={listingTitle || 'Bölge'}
                  data={filters.regions}
                  selectedItems={selectedRegions}
                  handleSelectedItems={onRegionChecked}
                />
              ) : null}

              {onlyAvailableHotels?.count ? (
                <HotelFilterCard>
                  <SingleSelectionFilter
                    title={
                      onlyAvailableHotels.value || 'Sadece Müsait Tesisler'
                    }
                    subTitle={onlyAvailableHotels.count}
                    checked={!!filters.onlyAvailableProperties}
                    mode='controlled'
                    name='status'
                    handleCheckBoxChange={(value) =>
                      handleSingleSelectionFilter(value, 'status')
                    }
                  />
                </HotelFilterCard>
              ) : null}

              {freeCancellation?.count ? (
                <HotelFilterCard>
                  <SingleSelectionFilter
                    title={freeCancellation.value || 'Ücretsiz İptal'}
                    subTitle={freeCancellation.count}
                    checked={!!filters.freeCancellation}
                    mode='controlled'
                    name='refundable'
                    handleCheckBoxChange={(value) =>
                      handleSingleSelectionFilter(value, 'refundable')
                    }
                  />
                </HotelFilterCard>
              ) : null}

              <HotelFilterCard>
                <NameFilter
                  inputWrapperClassName='listing-hotel-name-filter'
                  title='Otel Adı'
                  name={keywords}
                  setName={setKeywords}
                  placeholder='Otel adı ile arayın'
                  handleSearch={handleKeywordsFilter}
                  handleClearKeywordsFilter={handleClearKeywordsFilter}
                  withClear
                />
              </HotelFilterCard>

              {pointFilter?.[0]?.items?.length ? (
                <HotelFilterSectionWrapper>
                  <ReviewFilter
                    title='Misafir Puanı'
                    point={pointFilter}
                    handleSelectedItems={handleSelectedTripAdvisor}
                    selectedItems={query?.point || ''}
                    collapsible
                  />
                </HotelFilterSectionWrapper>
              ) : null}

              <HotelFilterSectionWrapper>
                <PriceRangeFilter
                  minValue={minAccPriceValue}
                  maxValue={maxAccPriceValue}
                  setMinValue={setMinAccPriceValue}
                  setMaxValue={setMaxAccPriceValue}
                  title='Konaklama Fiyatı'
                  handleSearch={handlePriceFilter}
                  collapsible
                />
              </HotelFilterSectionWrapper>

              {boardTypes?.length ? (
                <HotelFilterSection
                  title='Otel Konaklama Tipi'
                  data={boardTypes}
                  selectedItems={
                    filters.selectedAccommodationTypes?.map(
                      (item) => item.key
                    ) ?? selectedBoardTypes
                  }
                  handleSelectedItems={handleSelectedAccTypes}
                  collapsible
                />
              ) : null}

              {themes?.length ? (
                <HotelFilterSection
                  title='Otel Temaları'
                  data={themes}
                  selectedItems={selectedThemes}
                  handleSelectedItems={handleSelectedThemes}
                  collapsible
                />
              ) : null}

              {features?.length ? (
                <HotelFilterSection
                  title='Otel Özellikleri'
                  data={features}
                  selectedItems={selectedPopularities}
                  handleSelectedItems={handleSelectedPopularities}
                  collapsible
                />
              ) : null}

              {categories?.length ? (
                <HotelFilterSection
                  title='Otel Tipi'
                  data={categories}
                  selectedItems={selectedCategories}
                  handleSelectedItems={handleSelectedCategories}
                  handleRemoveItems={() =>
                    handleRemoveSpecificFilter('category')
                  }
                  collapsible
                />
              ) : null}

              {featureGroups.map((group) => (
                <HotelFilterSection
                  key={group.title}
                  title={group.title || 'Tesis Özellikleri'}
                  data={group.items}
                  selectedItems={selectedFeatures}
                  handleSelectedItems={handleSelectedFeatures}
                  collapsible
                />
              ))}
            </>
          )}
        </>
      )}
    </FiltersBlock>
  );
};

export default memo(ListingFilters);
